import {memo, useEffect, useState} from 'react';
import Image from 'next/image';
import {keyframes} from '@emotion/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import {borderRadius, borderWidth, colors, fontSize, fontWeight, lineHeight, overlay, shadows, transition} from '@/styles/tokens';

export interface RnFeature {
    title: string;
    /** URL of a single-colour SVG; it is recoloured through a CSS mask. */
    icon: string;
}

export interface RnFeatureSlide {
    src: string;
    alt: string;
}

interface RnFeatureShowcaseProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    items: RnFeature[];
    /** Single phone artwork. Ignored when `slides` is passed. */
    image?: string;
    imageAlt?: string;
    /**
     * Several phone artworks shown as a slider instead of `image`: they cross-fade in turn on their own.
     * All share `imageWidth` × `imageHeight`.
     */
    slides?: RnFeatureSlide[];
    /** Intrinsic size of the artwork, so its box is reserved before it loads. */
    imageWidth: number;
    imageHeight: number;
}

// How long each slide stays before the next fades in.
const SLIDE_MS = 3500;

// Copy and feature tiles on the left, the phone artwork on the right. The artwork drops
// out below `md`, where the tiles take the full width.
const layoutSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: 'minmax(0, 7fr) minmax(0, 4fr)'},
    alignItems: 'center',
    gap: {xs: 4, md: 6}
} as const;

const headerWrapSx = {maxWidth: 'none', mx: 0} as const;

const gridSx = {
    listStyle: 'none',
    m: 0,
    p: 0,
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))'},
    // Phones stack all the tiles in one column, so they sit tighter there.
    gap: {xs: 1, sm: 1.5}
} as const;

const TILE_TITLE_CLASS = 'rn-feature-tile-title';
const TILE_ICON_CLASS = 'rn-feature-tile-icon';

const tileSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 1.5,
    py: {xs: 1, sm: 1.5},
    bgcolor: colors.background.default,
    border: `${borderWidth.thin} solid ${colors.accent.blueBorder}`,
    borderRadius: borderRadius.lg,
    // Hover lifts the tile and fills it brand navy; the title and icon flip to white.
    transition: `transform ${transition.base}, background-color ${transition.base}, border-color ${transition.base}, box-shadow ${transition.base}`,
    '&:hover': {
        transform: 'translateY(-4px)',
        bgcolor: colors.primary.main,
        borderColor: colors.primary.main,
        boxShadow: shadows.lg,
        [`& .${TILE_TITLE_CLASS}`]: {color: colors.text.inverse},
        [`& .${TILE_ICON_CLASS}`]: {bgcolor: overlay.whiteLight, color: colors.text.inverse}
    },
    '@media (prefers-reduced-motion: reduce)': {'&:hover': {transform: 'none'}}
} as const;

const iconBoxSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: {xs: 28, sm: 32},
    height: {xs: 28, sm: 32},
    flexShrink: 0,
    borderRadius: borderRadius.md,
    bgcolor: colors.background.tag,
    color: colors.primary.main,
    transition: `background-color ${transition.base}, color ${transition.base}`
} as const;

// The supplied SVGs are black-stroked; as a mask over `currentColor` they read brand navy.
const maskedIconSx = (src: string) =>
    ({
        width: 18,
        height: 18,
        bgcolor: 'currentColor',
        maskImage: `url('${src}')`,
        WebkitMaskImage: `url('${src}')`,
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center'
    } as const);

const tileTitleSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight[20],
    color: colors.text.primary,
    transition: `color ${transition.base}`
} as const;

// A slow up-and-down drift so the phone reads as floating. Declared through Emotion: a
// keyframes block nested in `sx` is not hoisted to a real rule and never runs.
const float = keyframes({
    '0%, 100%': {transform: 'translateY(0)'},
    '50%': {transform: 'translateY(-14px)'}
});

const imageWrapSx = {
    display: {xs: 'none', md: 'flex'},
    justifyContent: 'center',
    // The artwork carries ~70px of empty canvas under the phone (its shadow). Pulling the
    // box up by that much stops the blank strip from padding out the section's bottom.
    mb: {md: -8},
    animation: `${float} 3s ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': {animation: 'none'}
} as const;
const imageStyle = {width: '100%', height: 'auto', maxWidth: 420} as const;

// Slider: the phone floats as above, one artwork cross-fading into the next.
const sliderWrapSx = {
    display: {xs: 'none', md: 'flex'},
    justifyContent: 'center',
    // Same as the single artwork: pull up over the blank shadow strip under the phone.
    mb: {md: -8}
} as const;

const sliderStageSx = (width: number, height: number) =>
    ({
        position: 'relative',
        width: '100%',
        maxWidth: 420,
        aspectRatio: `${width} / ${height}`,
        animation: `${float} 3s ease-in-out infinite`,
        '@media (prefers-reduced-motion: reduce)': {animation: 'none'}
    } as const);

const slideSx = (active: boolean) =>
    ({
        position: 'absolute',
        inset: 0,
        opacity: active ? 1 : 0,
        transition: 'opacity 600ms ease',
        '@media (prefers-reduced-motion: reduce)': {transition: 'none'}
    } as const);

const slideImageStyle = {width: '100%', height: '100%', objectFit: 'contain'} as const;

// Cross-fading phone artworks. Autoplays, pausing on hover; stays on the first slide for reduced motion. Every
// slide is in the HTML; only the first loads eagerly.
const PhoneSlider = ({slides, width, height}: {slides: RnFeatureSlide[]; width: number; height: number}) => {
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (paused || slides.length < 2 || globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
        const timer = setTimeout(() => setActive((i) => (i + 1) % slides.length), SLIDE_MS);
        return () => clearTimeout(timer);
    }, [active, paused, slides.length]);

    return (
        <Box sx={sliderWrapSx} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            <Box sx={sliderStageSx(width, height)}>
                {slides.map((slide, index) => (
                    <Box key={slide.src} sx={slideSx(index === active)} aria-hidden={index !== active}>
                        <Image
                            src={slide.src}
                            alt={slide.alt}
                            fill
                            sizes="(max-width: 900px) 0px, 420px"
                            // Served as-is, like the single artwork: the optimizer's re-encode softens the UI text.
                            unoptimized
                            loading={index === 0 ? 'eager' : 'lazy'}
                            style={slideImageStyle}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const RnFeatureShowcase = ({
    eyebrow,
    heading,
    subheading,
    items,
    image,
    imageAlt = '',
    slides,
    imageWidth,
    imageHeight
}: RnFeatureShowcaseProps) => (
    <MUIContainer>
        <Box sx={layoutSx}>
            <Box>
                <SectionHeader eyebrow={eyebrow} align="left" wrapperSx={headerWrapSx} subheading={subheading} heading={heading} />

                <Box component="ul" sx={gridSx}>
                    {items.map((item) => (
                        <Box component="li" key={item.title} sx={tileSx}>
                            <Box className={TILE_ICON_CLASS} sx={iconBoxSx} aria-hidden="true">
                                <Box sx={maskedIconSx(item.icon)} />
                            </Box>
                            <Typography component="h3" className={`font-headers ${TILE_TITLE_CLASS}`} sx={tileTitleSx}>
                                {item.title}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {slides?.length ? (
                <PhoneSlider slides={slides} width={imageWidth} height={imageHeight} />
            ) : (
                image && (
                    <Box sx={imageWrapSx}>
                        <Image
                            src={image}
                            alt={imageAlt}
                            width={imageWidth}
                            height={imageHeight}
                            sizes="(max-width: 900px) 0px, 420px"
                            // Served as-is: the artwork is already a compressed WebP, and the
                            // optimizer's quality-75 re-encode softened the UI text on it.
                            unoptimized
                            style={imageStyle}
                        />
                    </Box>
                )
            )}
        </Box>
    </MUIContainer>
);

export default memo(RnFeatureShowcase);
